import React, { useCallback, useEffect, useState } from "react";
import { Card, Badge, Button, Alert, Spinner, Row, Col, Label, Input, Table, FormGroup } from "reactstrap";
import { toast } from "react-toastify";
import moment from "moment";
import {
  hmrcFilingSettingsDataApi,
  hmrcFilingSettingsSaveApi,
  hmrcFilingCtCalculateApi,
  hmrcFilingCtPreviewApi,
  hmrcFilingCtSubmitApi,
  hmrcFilingSubmissionListApi,
  hmrcFilingSubmissionDataApi,
  hmrcFilingSubmissionCheckStatusApi,
} from "../../../../helpers/fakebackend_helper";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import AppModal from "../../../../Components/Common/AppModal";
import {
  fmtDate,
  fmtMoney,
  differs,
  EnvBadge,
  StatusBadge,
  OvertypeTable,
  IxbrlFrame,
  CodeBlock,
  prettyXml,
} from "./HmrcFilingShared";

// Change #100747 - Corporation Tax return (CT600).
//
// HMRC has no modern API for Corporation Tax, so the CT600 goes through the Government Gateway
// Transaction Engine as XML with the accounts and tax computations attached as iXBRL.
// CRMit starts from its own profit and loss, the accountant enters the tax adjustments, and
// any resulting figure can be overtyped before sending. Same shape as the Companies House
// filing: preview, send to the HMRC test service (or live once HMRC has recognised CRMit),
// check status, resubmit as an amended return, and every attempt kept.

const ADJUSTMENTS = [
  ["AddBackDepreciation", "Depreciation to add back"],
  ["AddBackDisallowable", "Disallowable expenses to add back"],
  ["CapitalAllowances", "Capital allowances claimed"],
  ["LossesBroughtForward", "Trading losses brought forward available"],
  ["AssociatedCompanies", "Associated companies"],
];

const emptyAdjustments = () => Object.fromEntries(ADJUSTMENTS.map(([k]) => [k, ""]));

const readFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = reject;
    reader.readAsText(file);
  });

export default function HmrcCorporationTax() {
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);
  const [settings, setSettings] = useState({
    ct_utr: "",
    ct_company_number: "",
    ct_company_name: "",
    gateway_user_id: "",
    declarant_name: "",
    declarant_status: "Director",
  });
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const [submissions, setSubmissions] = useState([]);
  const [prepare, setPrepare] = useState(null); // {} for new, or { initial, resubmits }
  const [viewing, setViewing] = useState(null);
  const [busy, setBusy] = useState("");

  const loadMeta = useCallback(async () => {
    try {
      const response = await hmrcFilingSettingsDataApi();
      if (response?.status === 200) {
        setMeta(response);
        setSettings((s) => ({
          ...s,
          ...Object.fromEntries(Object.entries(response.settings || {}).map(([k, v]) => [k, v ?? ""])),
          declarant_status: response.settings?.declarant_status || "Director",
        }));
      } else if (response?.message) {
        toast.error(response.error || response.message);
      }
    } catch (e) {
      toast.error("Could not load the HMRC filing details.");
    }
  }, []);

  const loadSubmissions = useCallback(async () => {
    try {
      const response = await hmrcFilingSubmissionListApi("CT600");
      if (response?.status === 200) setSubmissions(response.hmrcsubmission_list || []);
    } catch (e) {
      // non-fatal
    }
  }, []);

  useEffect(() => {
    (async () => {
      await Promise.all([loadMeta(), loadSubmissions()]);
      setLoading(false);
    })();
  }, [loadMeta, loadSubmissions]);

  const save = async () => {
    setSaving(true);
    try {
      const response = await hmrcFilingSettingsSaveApi([
        {
          Utr: settings.ct_utr,
          CompanyNumber: settings.ct_company_number,
          CompanyName: settings.ct_company_name,
          GatewayUserId: settings.gateway_user_id,
          GatewayPassword: password,
          DeclarantName: settings.declarant_name,
          DeclarantStatus: settings.declarant_status,
        },
      ]);
      if (response?.status === 200) {
        toast.success(response.message || "Saved");
        setPassword("");
        loadMeta();
      } else toast.error(response?.error || "Could not save.");
    } catch (e) {
      toast.error("Could not save.");
    }
    setSaving(false);
  };

  const checkStatus = async (id) => {
    setBusy(`check-${id}`);
    try {
      const response = await hmrcFilingSubmissionCheckStatusApi(id);
      if (response?.status === 200) {
        toast.info(response.message);
        loadSubmissions();
      } else toast.error(response?.error || "Could not check the status.");
    } catch (e) {
      toast.error("Could not check the status.");
    }
    setBusy("");
  };

  const load = async (id) => {
    const response = await hmrcFilingSubmissionDataApi(id);
    if (response?.status === 200) return response.hmrcsubmission_data;
    toast.error(response?.error || "Could not load the submission.");
    return null;
  };

  const openSubmission = async (id) => {
    setBusy(`view-${id}`);
    try {
      const s = await load(id);
      if (s) setViewing(s);
    } catch (e) {
      toast.error("Could not load the submission.");
    }
    setBusy("");
  };

  const resubmit = async (id) => {
    setBusy(`resubmit-${id}`);
    try {
      const s = await load(id);
      if (s) {
        setViewing(null);
        setPrepare({ initial: s, resubmits: s.Id });
      }
    } catch (e) {
      toast.error("Could not load the submission.");
    }
    setBusy("");
  };

  if (loading) {
    return (
      <Card className="p-2">
        <LoadingSpinner />
      </Card>
    );
  }

  const liveAvailable = Boolean(meta?.ct_live_available);
  const detailsReady = Boolean(meta?.settings?.ct_utr && meta?.settings?.ct_company_number && meta?.settings?.gateway_user_id && meta?.gateway_password_set);
  const set = (key) => (e) => setSettings((s) => ({ ...s, [key]: e.target.value }));

  return (
    <Card className="p-3">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
        <h5 className="mb-0">Corporation Tax (CT600)</h5>
        <Badge color={liveAvailable ? "success" : "warning"}>{liveAvailable ? "Live filing available" : "HMRC test service only"}</Badge>
      </div>
      <p className="text-muted mt-2 mb-0">
        CRMit starts from your profit and loss. You add the tax adjustments, and you can overtype any figure before sending. The return goes to HMRC
        with your accounts and tax computations attached as iXBRL. Every attempt is kept, showing what CRMit calculated and what was sent.
      </p>
      {!liveAvailable && (
        <Alert color="info" className="mt-3 mb-0 py-2">
          Live Corporation Tax filing opens once HMRC has recognised CRMit and issued its vendor ID. Until then, returns go to the HMRC External Test
          Service, which checks them fully but files nothing.
        </Alert>
      )}

      <hr />

      <h6 className="mb-3">Filing details</h6>
      <Row className="g-3">
        <Col md={3}>
          <Label className="form-label">Company UTR</Label>
          <Input value={settings.ct_utr} onChange={set("ct_utr")} placeholder="10 digits" maxLength={13} />
        </Col>
        <Col md={3}>
          <Label className="form-label">Company number</Label>
          <Input value={settings.ct_company_number} onChange={set("ct_company_number")} maxLength={8} />
        </Col>
        <Col md={6}>
          <Label className="form-label">Registered company name</Label>
          <Input value={settings.ct_company_name} onChange={set("ct_company_name")} />
        </Col>
        <Col md={3}>
          <Label className="form-label">Government Gateway user ID</Label>
          <Input value={settings.gateway_user_id} onChange={set("gateway_user_id")} autoComplete="off" />
        </Col>
        <Col md={3}>
          <Label className="form-label">Gateway password</Label>
          <Input
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={meta?.gateway_password_set ? "Saved - leave blank to keep" : ""}
          />
          <div className="form-text">The company's (or agent's) Corporation Tax login. Stored encrypted.</div>
        </Col>
        <Col md={3}>
          <Label className="form-label">Declaration made by</Label>
          <Input value={settings.declarant_name} onChange={set("declarant_name")} placeholder="Full name" />
        </Col>
        <Col md={3}>
          <Label className="form-label">Capacity</Label>
          <Input type="select" value={settings.declarant_status} onChange={set("declarant_status")}>
            {(meta?.declarant_statuses || ["Director"]).map((s) => (
              <option key={s}>{s}</option>
            ))}
          </Input>
        </Col>
      </Row>
      <div className="mt-3">
        <Button color="primary" onClick={save} disabled={saving}>
          {saving && <Spinner size="sm" className="me-1" />}
          Save filing details
        </Button>
      </div>

      <hr />

      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
        <h6 className="mb-0">CT600 returns</h6>
        <Button color="primary" onClick={() => setPrepare({})}>
          <i className="ri-file-upload-line me-1" />
          Prepare CT600
        </Button>
      </div>
      {!detailsReady && (
        <Alert color="info" className="py-2">
          You can prepare and preview now. To send, save the UTR, company number and Government Gateway login above.
        </Alert>
      )}

      {submissions.length === 0 ? (
        <p className="text-muted mb-0">No Corporation Tax returns have been sent from CRMit yet.</p>
      ) : (
        <div className="table-responsive">
          <Table size="sm" className="align-middle mb-0">
            <thead>
              <tr>
                <th>Sent</th>
                <th>Period</th>
                <th>Service</th>
                <th>Type</th>
                <th>Overtyped</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {submissions.map((s) => (
                <tr key={s.Id}>
                  <td>{fmtDate(s.CreatedDateTime, "DD MMM YYYY HH:mm")}</td>
                  <td>
                    {fmtDate(s.PeriodStartDate)} to {fmtDate(s.PeriodEndDate)}
                    {s.Resubmits___HmrcSubmission_Id && <div className="small text-muted">Resubmission of #{s.Resubmits___HmrcSubmission_Id}</div>}
                  </td>
                  <td>
                    <EnvBadge env={s.Environment} />
                  </td>
                  <td className="text-capitalize">{s.ReturnType}</td>
                  <td>{s.QtyOvertypes > 0 ? <Badge color="warning">{s.QtyOvertypes}</Badge> : <span className="text-muted small">none</span>}</td>
                  <td style={{ maxWidth: 360 }}>
                    <StatusBadge code={s.StatusCode} />
                    <span className="small text-muted">{s.StatusMessage}</span>
                    {s.LastStatusCheckDateTime && <div className="small text-muted">Checked {moment(s.LastStatusCheckDateTime).fromNow()}</div>}
                  </td>
                  <td className="text-end text-nowrap">
                    {s.StatusCode === "SUBMITTED" && (
                      <Button size="sm" color="primary" outline className="me-1" onClick={() => checkStatus(s.Id)} disabled={busy === `check-${s.Id}`}>
                        {busy === `check-${s.Id}` && <Spinner size="sm" className="me-1" />}
                        Check status
                      </Button>
                    )}
                    <Button size="sm" color="light" className="me-1" onClick={() => openSubmission(s.Id)} disabled={busy === `view-${s.Id}`}>
                      View
                    </Button>
                    <Button size="sm" color="primary" outline onClick={() => resubmit(s.Id)} disabled={busy === `resubmit-${s.Id}`}>
                      {busy === `resubmit-${s.Id}` ? <Spinner size="sm" className="me-1" /> : <i className="ri-refresh-line me-1" />}
                      {s.StatusCode === "ACCEPTED" ? "Amend" : "Resubmit"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {prepare && (
        <Ct600Modal
          prepare={prepare}
          meta={meta}
          settings={meta?.settings || {}}
          detailsReady={detailsReady}
          liveAvailable={liveAvailable}
          onClose={() => setPrepare(null)}
          onSent={() => {
            setPrepare(null);
            loadSubmissions();
          }}
        />
      )}

      <AppModal
        open={Boolean(viewing)}
        onClose={() => setViewing(null)}
        title={viewing ? `CT600 ${fmtDate(viewing.PeriodStartDate)} to ${fmtDate(viewing.PeriodEndDate)}` : ""}
        subtitle={viewing ? `${viewing.ReturnType === "amended" ? "Amended" : "Original"} return - sent ${fmtDate(viewing.CreatedDateTime, "DD MMM YYYY HH:mm")}` : ""}
        size="lg"
        hideCancel
        cancelLabel="Close"
      >
        {viewing && <SubmissionView s={viewing} labels={meta?.ct_field_labels || {}} onResubmit={() => resubmit(viewing.Id)} />}
      </AppModal>
    </Card>
  );
}

function SubmissionView({ s, labels, onResubmit }) {
  const [tab, setTab] = useState("figures");
  const calc = s.Calculated?.figures || {};
  const sent = s.Submitted?.figures || {};
  return (
    <>
      <p className="mb-2">
        <EnvBadge env={s.Environment} /> <StatusBadge code={s.StatusCode} />
        {s.StatusMessage}
      </p>
      <p className="small text-muted mb-2">
        UTR <span className="font-monospace">{s.TaxReference}</span> - IRmark <span className="font-monospace">{s.IrMark || "-"}</span>
        {s.CorrelationId && (
          <>
            {" "}- correlation <span className="font-monospace">{s.CorrelationId}</span>
          </>
        )}
      </p>
      {(s.Errors || []).length > 0 && (
        <Alert color="danger" className="py-2">
          <ul className="mb-0">
            {s.Errors.map((e, i) => (
              <li key={i}>
                {e.code && <span className="font-monospace me-1">{e.code}</span>}
                {e.text}
                {e.location && <span className="text-muted small"> ({e.location})</span>}
              </li>
            ))}
          </ul>
        </Alert>
      )}
      <div className="mb-2">
        {[
          ["figures", "Figures"],
          ["computations", "Computations"],
          ["accounts", "Accounts"],
          ["xml", "CT600 XML"],
          ["response", "HMRC response"],
        ].map(([k, l]) => (
          <Button key={k} size="sm" color={tab === k ? "primary" : "light"} className="me-1 mb-1" onClick={() => setTab(k)}>
            {l}
          </Button>
        ))}
      </div>
      {tab === "figures" && (
        <>
          <FigureComparison labels={labels} calculated={calc} submitted={sent} />
          <h6 className="mt-3">Overtypes</h6>
          <OvertypeTable overtypes={s.Overtypes} />
        </>
      )}
      {tab === "computations" && <IxbrlFrame html={s.ComputationsIxbrl} title="Tax computations" />}
      {tab === "accounts" && <IxbrlFrame html={s.AccountsIxbrl} title="Accounts" />}
      {tab === "xml" && <CodeBlock text={prettyXml(s.RequestPayload)} maxHeight={420} />}
      {tab === "response" && <CodeBlock text={prettyXml(s.ResponseBody) || "No response stored."} maxHeight={420} />}
      <div className="text-end mt-3">
        <Button color="primary" outline onClick={onResubmit}>
          <i className="ri-refresh-line me-1" />
          {s.StatusCode === "ACCEPTED" ? "Amend this return" : "Resubmit"}
        </Button>
      </div>
    </>
  );
}

function FigureComparison({ labels, calculated, submitted }) {
  return (
    <Table size="sm" className="align-middle mb-0">
      <thead>
        <tr>
          <th>Figure</th>
          <th className="text-end">CRMit calculated</th>
          <th className="text-end">Submitted</th>
        </tr>
      </thead>
      <tbody>
        {Object.entries(labels).map(([k, label]) => (
          <tr key={k} className={differs(calculated?.[k], submitted?.[k]) ? "table-warning" : ""}>
            <td>{label}</td>
            <td className="text-end">{fmtMoney(calculated?.[k])}</td>
            <td className="text-end fw-semibold">{fmtMoney(submitted?.[k])}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function Ct600Modal({ prepare, meta, settings, detailsReady, liveAvailable, onClose, onSent }) {
  const initial = prepare.initial;
  const labels = meta?.ct_field_labels || {};

  const defaultEnd = moment().subtract(1, "year").endOf("year");
  const [periodStart, setPeriodStart] = useState(initial?.PeriodStartDate?.slice(0, 10) || defaultEnd.clone().subtract(1, "year").add(1, "day").format("YYYY-MM-DD"));
  const [periodEnd, setPeriodEnd] = useState(initial?.PeriodEndDate?.slice(0, 10) || defaultEnd.format("YYYY-MM-DD"));
  const [adjustments, setAdjustments] = useState(() => {
    const a = initial?.Submitted?.adjustments;
    return a ? Object.fromEntries(ADJUSTMENTS.map(([k]) => [k, String(a[k] ?? "")])) : emptyAdjustments();
  });

  const [calc, setCalc] = useState(null); // { crm, figures, years, warnings, accounts_options }
  const [figures, setFigures] = useState(null); // strings, keyed like labels
  const [reasons, setReasons] = useState(() => {
    const r = {};
    (initial?.Overtypes || []).forEach((o) => {
      r[o.Field] = o.Reason || "";
    });
    return r;
  });

  const [accountsSource, setAccountsSource] = useState(initial?.Submitted?.attachments?.accounts || "companieshouse");
  const [chSubmissionId, setChSubmissionId] = useState(initial?.Submitted?.attachments?.companieshouse_submission_id || "");
  const [accountsIxbrl, setAccountsIxbrl] = useState(initial?.Submitted?.attachments?.accounts === "upload" ? initial?.AccountsIxbrl || "" : "");
  const [computationsSource, setComputationsSource] = useState(initial?.Submitted?.attachments?.computations || "generate");
  const [computationsIxbrl, setComputationsIxbrl] = useState(initial?.Submitted?.attachments?.computations === "upload" ? initial?.ComputationsIxbrl || "" : "");
  const [returnType, setReturnType] = useState(initial ? (initial.StatusCode === "ACCEPTED" || initial.ReturnType === "amended" ? "amended" : "new") : "new");

  const [step, setStep] = useState("period"); // period | figures | preview
  const [preview, setPreview] = useState(null);
  const [previewTab, setPreviewTab] = useState("checks");
  const [live, setLive] = useState(false);
  const [confirmUtr, setConfirmUtr] = useState("");
  const [declaration, setDeclaration] = useState(false);
  const [busy, setBusy] = useState(false);

  const adjustmentPayload = () => Object.fromEntries(ADJUSTMENTS.map(([k]) => [k, adjustments[k] === "" ? 0 : adjustments[k]]));

  const doCalculate = async () => {
    setBusy(true);
    try {
      const response = await hmrcFilingCtCalculateApi([{ PeriodStartDate: periodStart, PeriodEndDate: periodEnd, Adjustments: adjustmentPayload() }]);
      if (response?.status === 200) {
        setCalc(response);
        // A resubmission keeps the figures that were sent; otherwise start from CRMit's.
        const prior = initial?.Submitted?.figures;
        setFigures(Object.fromEntries(Object.keys(labels).map((k) => [k, String(prior && figures === null ? prior[k] ?? response.figures[k] : response.figures[k])])));
        if (!chSubmissionId && response.accounts_options?.length) setChSubmissionId(String(response.accounts_options[0].Id));
        setStep("figures");
      } else toast.error(response?.error || "Could not work out the figures.");
    } catch (e) {
      toast.error("Could not work out the figures.");
    }
    setBusy(false);
  };

  const payload = () => ({
    PeriodStartDate: periodStart,
    PeriodEndDate: periodEnd,
    Adjustments: adjustmentPayload(),
    Figures: figures,
    Reasons: reasons,
    ReturnType: returnType,
    Attachments: {
      AccountsSource: accountsSource,
      ...(accountsSource === "companieshouse" ? { CompaniesHouseSubmission_Id: chSubmissionId } : { AccountsIxbrl: accountsIxbrl }),
      ComputationsSource: computationsSource,
      ...(computationsSource === "upload" ? { ComputationsIxbrl: computationsIxbrl } : {}),
    },
  });

  const doPreview = async () => {
    setBusy(true);
    try {
      const response = await hmrcFilingCtPreviewApi([payload()]);
      if (response?.status === 200) {
        setPreview(response);
        setPreviewTab("checks");
        setStep("preview");
      } else toast.error(response?.error || "Could not build the preview.");
    } catch (e) {
      toast.error("Could not build the preview.");
    }
    setBusy(false);
  };

  const doSubmit = async () => {
    setBusy(true);
    try {
      const response = await hmrcFilingCtSubmitApi([
        {
          ...payload(),
          Declaration: true,
          Live: live,
          ConfirmUtr: live ? confirmUtr : "",
          ...(prepare.resubmits ? { Resubmits___HmrcSubmission_Id: prepare.resubmits } : {}),
        },
      ]);
      if (response?.status === 200) {
        toast.success(response.message || "Sent to HMRC");
        onSent();
        return;
      }
      toast.error(response?.error || "HMRC did not accept the return.");
      if (response?.submission) onSent();
    } catch (e) {
      toast.error("Could not reach CRMit to send the return.");
    }
    setBusy(false);
  };

  const pickFile = (setter) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setter(await readFile(file));
      toast.success(`${file.name} loaded`);
    } catch (err) {
      toast.error("Could not read that file.");
    }
  };

  // Recompute the derived figures from the inputs when the user changes an input, like the server does.
  const resetToCalculated = (k) => setFigures((f) => ({ ...f, [k]: String(calc.figures[k]) }));

  const liveConfirmed = !live || confirmUtr.replace(/\D/g, "") === settings.ct_utr;
  const blocking = [...(preview?.missing || []), ...(preview?.xml_errors || [])];

  const footer =
    step === "period" ? (
      <>
        <Button color="light" onClick={onClose}>
          Cancel
        </Button>
        <Button color="primary" onClick={doCalculate} disabled={busy || !periodStart || !periodEnd}>
          Work out figures
        </Button>
      </>
    ) : step === "figures" ? (
      <>
        <Button color="light" onClick={() => setStep("period")} disabled={busy}>
          Back
        </Button>
        <Button color="primary" onClick={doPreview} disabled={busy}>
          Preview CT600
        </Button>
      </>
    ) : (
      <>
        <Button color="light" onClick={() => setStep("figures")} disabled={busy}>
          Back
        </Button>
        <Button color={live ? "danger" : "primary"} onClick={doSubmit} disabled={busy || !declaration || !liveConfirmed || blocking.length > 0 || !detailsReady}>
          {live ? "File with HMRC" : "Send to HMRC test service"}
        </Button>
      </>
    );

  return (
    <AppModal
      open
      onClose={onClose}
      title={`${prepare.resubmits ? (returnType === "amended" ? "Amend" : "Resubmit") : "Prepare"} CT600`}
      subtitle={`${settings.ct_company_name || ""} ${settings.ct_company_number ? `(${settings.ct_company_number})` : ""}`}
      size="lg"
      loading={busy}
      footer={footer}
    >
      {step === "period" && (
        <>
          <Row className="g-3 mb-3">
            <Col md={4}>
              <Label className="form-label">Accounting period start</Label>
              <Input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
            </Col>
            <Col md={4}>
              <Label className="form-label">Accounting period end</Label>
              <Input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
            </Col>
          </Row>
          <h6>Tax adjustments</h6>
          <p className="small text-muted">CRMit takes turnover and profit from your profit and loss. Enter the adjustments from your accountant here, or leave them at 0.</p>
          <Row className="g-3">
            {ADJUSTMENTS.map(([k, label]) => (
              <Col md={4} key={k}>
                <Label className="form-label small">{label}</Label>
                <Input type="number" step={k === "AssociatedCompanies" ? "1" : "0.01"} className="text-end" value={adjustments[k]} onChange={(e) => setAdjustments((a) => ({ ...a, [k]: e.target.value }))} />
              </Col>
            ))}
          </Row>
        </>
      )}

      {step === "figures" && calc && figures && (
        <>
          {(calc.warnings || []).map((w, i) => (
            <Alert key={i} color="warning" className="py-2">
              {w}
            </Alert>
          ))}
          <p className="small text-muted mb-2">
            From the CRM: turnover {fmtMoney(calc.crm?.Turnover)}, profit before tax {fmtMoney(calc.crm?.ProfitBeforeTax)}. Overtype any figure your accountant has
            arrived at differently. The tax-by-year lines are recalculated from the chargeable profits you send.
          </p>
          <div className="table-responsive">
            <Table size="sm" className="align-middle">
              <thead>
                <tr>
                  <th>Figure</th>
                  <th className="text-end" style={{ width: 130 }}>
                    CRMit
                  </th>
                  <th className="text-end" style={{ width: 150 }}>
                    To submit
                  </th>
                  <th style={{ width: 220 }}>Reason for change</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(labels).map(([k, label]) => {
                  const changed = differs(calc.figures[k], figures[k]);
                  return (
                    <tr key={k} className={changed ? "table-warning" : ""}>
                      <td className="small">{label}</td>
                      <td className="text-end">{fmtMoney(calc.figures[k])}</td>
                      <td>
                        <Input type="number" step={k === "AssociatedCompanies" ? "1" : "0.01"} className="text-end" value={figures[k]} onChange={(e) => setFigures((f) => ({ ...f, [k]: e.target.value }))} />
                      </td>
                      <td>
                        {changed && (
                          <>
                            <Input bsSize="sm" value={reasons[k] || ""} placeholder="Why it differs" onChange={(e) => setReasons((r) => ({ ...r, [k]: e.target.value }))} />
                            <Button size="sm" color="link" className="p-0 small" onClick={() => resetToCalculated(k)}>
                              Use CRMit figure
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          <h6 className="mt-3">Tax by financial year (CRMit)</h6>
          <Table size="sm" className="mb-3">
            <tbody>
              {(calc.years || []).map((y) => (
                <tr key={y.Year}>
                  <td>
                    FY{y.Year} ({y.Days} days, {y.Band} rate)
                  </td>
                  <td className="text-end">
                    {fmtMoney(y.Profit)} at {y.TaxRate}% = {fmtMoney(y.Tax)}
                    {y.MarginalRelief > 0 && ` less marginal relief ${fmtMoney(y.MarginalRelief)}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <h6>Attachments</h6>
          <Row className="g-3">
            <Col md={6}>
              <Label className="form-label">Accounts (iXBRL)</Label>
              <Input type="select" value={accountsSource} onChange={(e) => setAccountsSource(e.target.value)}>
                <option value="companieshouse">Use a Companies House filing from CRMit</option>
                <option value="upload">Upload the accountant's iXBRL file</option>
              </Input>
              {accountsSource === "companieshouse" ? (
                (calc.accounts_options || []).length === 0 ? (
                  <div className="form-text text-danger">No Companies House filings in CRMit yet. Upload the accounts instead.</div>
                ) : (
                  <Input type="select" className="mt-2" value={chSubmissionId} onChange={(e) => setChSubmissionId(e.target.value)}>
                    {calc.accounts_options.map((o) => (
                      <option key={o.Id} value={o.Id}>
                        Period ending {fmtDate(o.PeriodEndDate)} - {o.Environment} - {o.StatusCode} ({o.SubmissionNumber})
                      </option>
                    ))}
                  </Input>
                )
              ) : (
                <>
                  <Input type="file" className="mt-2" accept=".html,.htm,.xhtml,.xml" onChange={pickFile(setAccountsIxbrl)} />
                  {accountsIxbrl && <div className="form-text">{accountsIxbrl.length.toLocaleString()} characters loaded.</div>}
                </>
              )}
            </Col>
            <Col md={6}>
              <Label className="form-label">Tax computations (iXBRL)</Label>
              <Input type="select" value={computationsSource} onChange={(e) => setComputationsSource(e.target.value)}>
                <option value="generate">Generate from these figures</option>
                <option value="upload">Upload the accountant's iXBRL file</option>
              </Input>
              {computationsSource === "upload" && (
                <>
                  <Input type="file" className="mt-2" accept=".html,.htm,.xhtml,.xml" onChange={pickFile(setComputationsIxbrl)} />
                  {computationsIxbrl && <div className="form-text">{computationsIxbrl.length.toLocaleString()} characters loaded.</div>}
                </>
              )}
            </Col>
            <Col md={6}>
              <Label className="form-label">Return type</Label>
              <Input type="select" value={returnType} onChange={(e) => setReturnType(e.target.value)}>
                <option value="new">Original return</option>
                <option value="amended">Amended return</option>
              </Input>
            </Col>
          </Row>
        </>
      )}

      {step === "preview" && preview && (
        <>
          {blocking.length > 0 && (
            <Alert color="danger" className="py-2">
              <ul className="mb-0">
                {blocking.map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            </Alert>
          )}
          {(preview.warnings || []).length > 0 && (
            <Alert color="warning" className="py-2">
              <ul className="mb-0">
                {preview.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </Alert>
          )}
          <div className="mb-2">
            {[
              ["checks", "Figures"],
              ["computations", "Computations"],
              ["accounts", "Accounts"],
              ["xml", "CT600 XML"],
            ].map(([k, l]) => (
              <Button key={k} size="sm" color={previewTab === k ? "primary" : "light"} className="me-1 mb-1" onClick={() => setPreviewTab(k)}>
                {l}
              </Button>
            ))}
          </div>
          {previewTab === "checks" && (
            <>
              <FigureComparison labels={labels} calculated={preview.calculated} submitted={preview.submitted} />
              <h6 className="mt-3">Overtypes</h6>
              <OvertypeTable overtypes={preview.overtypes} />
              <p className="small text-muted mb-0">
                IRmark <span className="font-monospace">{preview.irmark}</span>
              </p>
            </>
          )}
          {previewTab === "computations" && <IxbrlFrame html={preview.computations_ixbrl} title="Tax computations" />}
          {previewTab === "accounts" && <IxbrlFrame html={preview.accounts_ixbrl} title="Accounts" />}
          {previewTab === "xml" && <CodeBlock text={prettyXml(preview.xml)} maxHeight={420} />}

          <div className="mt-3">
            <Label className="form-label d-block">Send to</Label>
            <FormGroup check inline>
              <Input type="radio" id="ct-test" checked={!live} onChange={() => setLive(false)} />
              <Label check for="ct-test">
                HMRC External Test Service
              </Label>
            </FormGroup>
            <FormGroup check inline>
              <Input type="radio" id="ct-live" checked={live} onChange={() => setLive(true)} disabled={!liveAvailable} />
              <Label check for="ct-live">
                Live{!liveAvailable ? " (not available until HMRC recognises CRMit)" : ""}
              </Label>
            </FormGroup>
          </div>
          <FormGroup check className="mt-2">
            <Input type="checkbox" id="ct-declaration" checked={declaration} onChange={(e) => setDeclaration(e.target.checked)} />
            <Label check for="ct-declaration" className="small">
              The information in this return and its attachments is correct and complete to the best of my knowledge and belief. I understand that
              giving false information could lead to prosecution.
            </Label>
          </FormGroup>
          {live && (
            <Alert color="danger" className="mt-2 mb-0">
              This files the return with HMRC. Type the company UTR <strong>{settings.ct_utr}</strong> to confirm.
              <Input className="mt-2" value={confirmUtr} onChange={(e) => setConfirmUtr(e.target.value)} placeholder={settings.ct_utr} />
            </Alert>
          )}
          {!detailsReady && (
            <Alert color="info" className="mt-2 mb-0 py-2">
              Save the UTR, company number and Government Gateway login under Filing details to send.
            </Alert>
          )}
        </>
      )}
    </AppModal>
  );
}
